import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * Feature Flags Context
 * Allows toggling features on/off across the application
 */

export interface FeatureFlags {
  // Admin Features
  adminVerify: boolean;
  adminOrg: boolean;
  adminCatalog: boolean;
  adminResponses: boolean;
  
  // Patient Features
  patientVerification: boolean;
  reviewSubmission: boolean;
  reviewPhotos: boolean;
  reviewVoting: boolean;
  
  // Clinic Features
  clinicQrGeneration: boolean;
  clinicDashboard: boolean;
  
  // General Features
  demoMode: boolean;
  debugBanner: boolean;
  devModePanel: boolean;
  
  // Experimental Features
  batchTracking: boolean;
  pharmacyIntegration: boolean;
  aiModeration: boolean;
}

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  isEnabled: (feature: keyof FeatureFlags) => boolean;
  enableFeature: (feature: keyof FeatureFlags) => void;
  disableFeature: (feature: keyof FeatureFlags) => void;
  toggleFeature: (feature: keyof FeatureFlags) => void;
  resetToDefaults: () => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

// Default feature flags
const DEFAULT_FLAGS: FeatureFlags = {
  // Admin Features - All enabled
  adminVerify: true,
  adminOrg: true,
  adminCatalog: true,
  adminResponses: true,
  
  // Patient Features - All enabled
  patientVerification: true,
  reviewSubmission: true,
  reviewPhotos: true,
  reviewVoting: true,
  
  // Clinic Features - All enabled
  clinicQrGeneration: true,
  clinicDashboard: true,
  
  // General Features
  demoMode: true,
  debugBanner: true, // Show in non-production
  devModePanel: true,
  
  // Experimental Features - Can be toggled
  batchTracking: true,
  pharmacyIntegration: true,
  aiModeration: false, // Not implemented yet
};

const STORAGE_KEY = 'cannabias_feature_flags';

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    // Load from localStorage if available
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure new flags exist
        return { ...DEFAULT_FLAGS, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load feature flags:', error);
    }
    return DEFAULT_FLAGS;
  });

  // Persist to localStorage whenever flags change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
      console.log('🚩 Feature flags updated:', flags);
    } catch (error) {
      console.error('Failed to save feature flags:', error);
    }
  }, [flags]);

  const isEnabled = (feature: keyof FeatureFlags): boolean => {
    return flags[feature] === true;
  };

  const enableFeature = (feature: keyof FeatureFlags) => {
    setFlags(prev => ({ ...prev, [feature]: true }));
    console.log(`✅ Feature enabled: ${feature}`);
  };

  const disableFeature = (feature: keyof FeatureFlags) => {
    setFlags(prev => ({ ...prev, [feature]: false }));
    console.log(`❌ Feature disabled: ${feature}`);
  };

  const toggleFeature = (feature: keyof FeatureFlags) => {
    setFlags(prev => ({ ...prev, [feature]: !prev[feature] }));
    console.log(`🔄 Feature toggled: ${feature} -> ${!flags[feature]}`);
  };

  const resetToDefaults = () => {
    setFlags(DEFAULT_FLAGS);
    console.log('🔄 Feature flags reset to defaults');
  };

  return (
    <FeatureFlagsContext.Provider
      value={{
        flags,
        isEnabled,
        enableFeature,
        disableFeature,
        toggleFeature,
        resetToDefaults,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
}

// Convenience hook for checking a single feature
export function useFeature(feature: keyof FeatureFlags): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(feature);
}
